<template>
  <v-form ref="form" @submit.prevent>
    <FormSection>
      <ComplaintFormTabs
        v-model="modelType"
        :items="modelTypeItems"
        :required="true"
        :error-messages="modelTypeErrors"
        @tab-click="onModelTypeClick"
      >
        <template #card-content="{ item }">
          <BaseHeading level="3" class="mb-0 ml-2">{{ item.text }}</BaseHeading>
        </template>
      </ComplaintFormTabs>
    </FormSection>

    <FormSection :disabled="!modelType" :hidden-disabled-mobile="true">
      <FormSection
        v-if="modelType === MODEL_TYPE_BATTERY || modelType === MODEL_TYPE_BATTERY_AND_LOADER"
      >
        <BaseHeading :level="3" class="mb-1">
          {{ $t('complaintForm.ticket.battery.data_battery') }}
        </BaseHeading>

        <BaseTextfield
          v-model="formData.batteryArticleNo"
          class="mb-6"
          :error-messages="batteryArticleNoErrors"
          :hide-details="!batteryArticleNoErrors.length"
          :label="$t('complaintForm.ticket.battery.item_number')"
          counter
          required
          v-on="getTouchListeners('formData.batteryArticleNo')"
        />

        <BaseTextfield
          v-model="formData.batterySerialNo"
          class="mb-6"
          :error-messages="batterySerialNoErrors"
          :hide-details="!batterySerialNoErrors.length"
          :label="$t('complaintForm.ticket.battery.serial_number')"
          counter
          required
          v-on="getTouchListeners('formData.batterySerialNo')"
        />

        <BaseAlert type="warning" icon="info" :message="modelBatteryTypeInfo.message">
          <v-img
            v-if="modelBatteryTypeInfo.image"
            :min-height="190"
            :max-height="190"
            :src="modelBatteryTypeInfo.image.imageUrl"
            :lazy-src="modelBatteryTypeInfo.image.imageUrl"
            :alt="modelBatteryTypeInfo.image.name"
            contain
            position="top center"
          />
        </BaseAlert>
      </FormSection>

      <FormSection
        v-if="modelType === MODEL_TYPE_LOADER || modelType === MODEL_TYPE_BATTERY_AND_LOADER"
      >
        <BaseHeading :level="3" class="mb-1">
          {{ $t('complaintForm.ticket.battery.data_charger') }}
        </BaseHeading>

        <BaseTextfield
          v-model="formData.loaderArticleNo"
          class="mb-6"
          :error-messages="loaderArticleNoErrors"
          :hide-details="!loaderArticleNoErrors.length"
          :label="$t('complaintForm.ticket.battery.item_number')"
          counter
          required
          v-on="getTouchListeners('formData.loaderArticleNo')"
        />

        <BaseTextfield
          v-model="formData.loaderYearOfConstruction"
          class="mb-6"
          :error-messages="loaderYearOfConstructionErrors"
          :hide-details="!loaderYearOfConstructionErrors.length"
          :label="$t('complaintForm.ticket.battery.construction_year')"
          counter
          required
          v-on="getTouchListeners('formData.loaderYearOfConstruction')"
        />

        <BaseAlert type="warning" icon="info" :message="modelLoaderTypeInfo.message">
          <v-img
            v-if="modelLoaderTypeInfo.image"
            :min-height="250"
            :max-height="250"
            :src="modelLoaderTypeInfo.image.imageUrl"
            :lazy-src="modelLoaderTypeInfo.image.imageUrl"
            :alt="modelLoaderTypeInfo.image.name"
            contain
            position="top center"
          />
        </BaseAlert>
      </FormSection>

      <FormSection v-if="isWarantyType || isProductLiabilityType">
        <!-- TODO: UPDATE (https://docs.google.com/spreadsheets/d/1CbT1j1d7aHwhHRH3x7f4oWtAoQh2n00mlCuKkjFiECI/edit#gid=0) -->

        <BaseTextfield
          v-model="serialNumberBike"
          v-mask="'###### ###'"
          class="mb-6"
          :error-messages="serialNumberBikeErrors"
          :hide-details="!serialNumberBikeErrors.length"
          :label="$t('complaintForm.ticket.engine.serial_number')"
          required
          v-on="getTouchListeners('serialNumberBike')"
        />

        <BaseAlert
          type="warning"
          icon="info"
          :message="$t('complaintForm.ticket.engine.explain_serial')"
        >
          <v-img
            v-if="imageData.serial"
            :min-height="190"
            :max-height="190"
            :src="imageData.serial.imageUrl"
            :lazy-src="imageData.serial.imageUrl"
            :alt="imageData.serial.name"
            cover
            position="top center"
          />
        </BaseAlert>
      </FormSection>

      <FormSection>
        <BaseSelect
          v-model="formData.calculationDesire"
          class="mb-6"
          :error-messages="calculationDesireErrors"
          :hide-details="!calculationDesireErrors.length"
          :label="$t('complaintForm.ticket.engine.calculation_wish')"
          :items="calculationWishSelectItems"
          required
          v-on="getTouchListeners('formData.calculationDesire')"
        />

        <template v-if="modelType === MODEL_TYPE_BATTERY_AND_LOADER && isRepairJobType">
          <BaseAlert
            type="warning"
            icon="info"
            :message="$t('complaintForm.ticket.battery.repair_job_disclaimer')"
          ></BaseAlert>
        </template>

        <template v-if="isRepairJobType && modelType !== MODEL_TYPE_LOADER">
          <BaseRichtext v-if="batteryCostTable" :html="batteryCostTable" />
        </template>

        <template
          v-if="
            (modelType === MODEL_TYPE_LOADER || modelType === MODEL_TYPE_BATTERY_AND_LOADER) &&
            isWarantyType
          "
        >
          <BaseAlert
            type="warning"
            icon="info"
            :message="$t('complaintForm.ticket.battery.warranty_disclaimer')"
          ></BaseAlert>
        </template>

        <template v-if="isWarantyType || isProductLiabilityType">
          <v-row>
            <v-col class="py-0">
              <BaseHeading :level="3" class="mb-1 required">{{
                $t('complaintForm.ticket.engine.sale_date')
              }}</BaseHeading>

              <BaseDatepicker
                v-model="formData.saleDate"
                class="mb-6"
                :error-messages="saleDateErrors"
                :hide-details="!saleDateErrors.length"
                :locale="language"
                :show-current="false"
                :max="currentDate"
                required
                full-width
                landscape
                v-on="getTouchListeners('formData.saleDate')"
              />
            </v-col>

            <v-col class="py-0">
              <BaseHeading :level="3" class="mb-1 required">{{
                $t('complaintForm.ticket.engine.purchase_receipt')
              }}</BaseHeading>

              <FileDropzone
                v-model="proofOfPurchase"
                class="mb-6"
                :error-messages="fileUploadErrors"
                :hide-details="!fileUploadErrors.length"
                url="/api/bureturnapi?code=image-upload"
                required
                @[onDropzoneSuccess]="setFileUploadPath"
                @[onDropzoneSending]="setPending(true)"
                @[onDropzoneComplete]="setPending(false)"
              />
            </v-col>
          </v-row>

          <template v-if="isProductLiabilityType">
            <v-row>
              <v-col class="py-0">
                <BaseHeading :level="3" class="mb-1 required">{{
                  $t('complaintForm.ticket.component.accident_date')
                }}</BaseHeading>

                <BaseDatepicker
                  v-model="formData.accidentDate"
                  class="mb-6"
                  :error-messages="accidentDateErrors"
                  :hide-details="!accidentDateErrors.length"
                  :locale="language"
                  :show-current="false"
                  :max="currentDate"
                  required
                  full-width
                  landscape
                  v-on="getTouchListeners('formData.accidentDate')"
                />
              </v-col>

              <v-col class="py-0 pt-8">
                <BaseTextarea
                  v-model="formData.accidentReport"
                  rows="15"
                  :error-messages="accidentReportErrors"
                  :hide-details="!accidentReportErrors.length"
                  :label="$t('complaintForm.ticket.component.accident_report')"
                  counter
                  required
                  v-on="getTouchListeners('formData.accidentReport')"
                />
              </v-col>
            </v-row>
          </template>
        </template>
      </FormSection>

      <FormSection v-if="!canBeClaimed">
        <BaseAlert type="warning" icon="info" :message="$t('complaintForm.claimNotPossible')" />
      </FormSection>
    </FormSection>

    <ComplaintFormStepperControls
      :is-valid="isValid"
      :has-required-fields="true"
      :prev-step="prevStep"
      :next-step="submit"
    />
  </v-form>
</template>

<script>
import { compareAsc, subYears, parseISO } from 'date-fns'
import { get, replace, compact } from 'lodash-es'
import { mapMutations, mapGetters } from 'vuex'
import { mask } from 'vue-the-mask'
import {
  required,
  numeric,
  minLength,
  maxLength,
  requiredIf,
  requiredUnless,
} from 'vuelidate/lib/validators'

import ComplaintFormImageDataMixin from '@/js/components/Forms/ComplaintForm/_mixins/complaint-form-image-data-mixin.js'

import StepperMixin from '@/js/mixins/stepper-mixin.js'
import FormValidationMixin from '@/js/components/Forms/mixins/form-validation-mixin.js'
import ComplaintFormMixin from '@/js/components/Forms/ComplaintForm/_mixins/complaint-form-default-mixin.js'

import FORM_CONSTANTS from '@/js/components/Forms/ComplaintForm/_store-module/constants.js'
import FORM_MUTATION from '@/js/components/Forms/ComplaintForm/_store-module/mutation-types.js'
import FORM_GETTER from '@/js/components/Forms/ComplaintForm/_store-module/getter-types.js'

import FormSection from '@/js/components/Forms/FormSection.vue'
import FileDropzone, { FILE_DROPZONE_EVENTS } from '@/js/components/Shared/FileDropzone.vue'

import ComplaintFormTabs from '../../ComplaintFormTabs.vue'
import ComplaintFormStepperControls from '../../ComplaintFormStepperControls.vue'

export const COMPLAINT_FORM_BATTERY_DATA_CONSTANTS = {
  MODEL_TYPE_BATTERY: 'battery_pack',
  MODEL_TYPE_LOADER: 'loader',
  MODEL_TYPE_BATTERY_AND_LOADER: 'battery_and_loader',
}

export default {
  name: 'ComplaintFormBatteryProductData',
  components: {
    FormSection,
    ComplaintFormTabs,
    ComplaintFormStepperControls,
    FileDropzone,
  },
  directives: {
    mask,
  },
  mixins: [FormValidationMixin, ComplaintFormMixin, ComplaintFormImageDataMixin],
  props: {
    ...StepperMixin.props,
  },
  data() {
    return {
      // Constants
      ...COMPLAINT_FORM_BATTERY_DATA_CONSTANTS,
      onDropzoneSuccess: FILE_DROPZONE_EVENTS.VDROPZONE_SUCCESS,
      onDropzoneSending: FILE_DROPZONE_EVENTS.VDROPZONE_SENDING,
      onDropzoneComplete: FILE_DROPZONE_EVENTS.VDROPZONE_COMPLETE,
      formData: {
        batteryArticleNo: null,
        batterySerialNo: null,
        loaderArticleNo: null,
        loaderYearOfConstruction: null,
        calculationDesire: null,
        saleDate: null,
        fileUpload: '',
        accidentDate: null,
        accidentReport: null,
      },
      // Other data
      currentDate: new Date().toISOString().substr(0, 10),
      proofOfPurchase: null,
      proofOfPurchaseError: '',
      serialNumberBike: null,
      modelType: null,
      prevModelType: null,
      // validation values
      batteryArticleNoMinLength: 5,
      batterySerialNoMinLength: 5,
      loaderArticleNoMinLength: 5,
      loaderYearOfConstructionMinLength: 4,
      serialNumberBikeMaxLength: 9,
    }
  },
  validations: {
    serialNumberBike: {
      required: requiredUnless(function () {
        return this.isRepairJobType
      }),
      numeric: {
        value(val) {
          return numeric(replace(val, ' ', ''))
        },
      },
      minLength: minLength(10), // +1 because of space
      maxLength: maxLength(10), // +1 because of space
    },
    modelType: {
      required,
    },
    formData: {
      batteryArticleNo: {
        required: requiredUnless(function () {
          return this.isLoaderModelType
        }),
        numeric,
        minLength: minLength(5),
      },
      batterySerialNo: {
        required: requiredUnless(function () {
          return this.isLoaderModelType
        }),
        minLength: minLength(5),
      },
      loaderArticleNo: {
        required: requiredUnless(function () {
          return this.isBatteryModelType
        }),
        numeric,
        minLength: minLength(5),
      },
      loaderYearOfConstruction: {
        required: requiredUnless(function () {
          return this.isBatteryModelType
        }),
        numeric,
        minLength: minLength(4),
      },
      calculationDesire: {
        required,
      },
      saleDate: {
        required: requiredUnless(function () {
          return this.isRepairJobType
        }),
      },
      fileUpload: {
        required: requiredUnless(function () {
          return this.isRepairJobType
        }),
      },
      accidentDate: {
        required: requiredIf(function () {
          return this.isProductLiabilityType
        }),
      },
      accidentReport: {
        required: requiredIf(function () {
          return this.isProductLiabilityType
        }),
      },
    },
  },
  computed: {
    ...mapGetters(FORM_CONSTANTS.STORE_ID, {
      language: FORM_GETTER.LANGUAGE,
      steps: FORM_GETTER.STEPS,
    }),

    /** Computed helper */
    serialNo1() {
      return get(this.serialNumberBike?.split(' '), '0', '')
    },
    serialNo2() {
      return get(this.serialNumberBike?.split(' '), '1', '')
    },
    translatedModelType() {
      return this.$t(`complaintForm.ticket.battery.${this.modelType}`)
    },

    /** Model Types  */
    isBatteryModelType() {
      return this.modelType === COMPLAINT_FORM_BATTERY_DATA_CONSTANTS.MODEL_TYPE_BATTERY
    },
    isLoaderModelType() {
      return this.modelType === COMPLAINT_FORM_BATTERY_DATA_CONSTANTS.MODEL_TYPE_LOADER
    },

    /** Calculation Desire Types */
    isRepairJobType() {
      return (
        this.formData.calculationDesire ===
        FORM_CONSTANTS.CALCULATION_DESIRE_TYPES.CALCULATION_DESIRE_REPAIR_JOB
      )
    },
    isWarantyType() {
      return (
        this.formData.calculationDesire ===
        FORM_CONSTANTS.CALCULATION_DESIRE_TYPES.CALCULATION_DESIRE_GUARANTEE
      )
    },
    isProductLiabilityType() {
      return (
        this.formData.calculationDesire ===
        FORM_CONSTANTS.CALCULATION_DESIRE_TYPES.CALCULATION_DESIRE_PRODUCT_LIABILITY
      )
    },

    canBeClaimed() {
      return this.formData.saleDate
        ? compareAsc(new Date(this.formData.saleDate), subYears(parseISO(this.currentDate), 3)) >= 0
        : true
    },
    modelBatteryTypeInfo() {
      return {
        message: this.$t('complaintForm.ticket.battery.series_no_find_1'),
        image: get(this.imageData, 'batterySticker', {}),
      }
    },
    modelLoaderTypeInfo() {
      return {
        message: this.$t('complaintForm.ticket.battery.series_no_find_1'),
        image: get(this.imageData, 'ticketCorrections', {}),
      }
    },

    /** Form data */
    fullFormData() {
      return {
        ...this.formData,
        modelType: this.translatedModelType,
        serialNo1: this.serialNo1,
        serialNo2: this.serialNo2,
      }
    },
    batteryStepData() {
      return get(this.steps, FORM_CONSTANTS.SOURCE_DATA_CONSTANTS.STEPS.PRODUCT_INFOS)
    },
    batteryCostTable() {
      return get(this.batteryStepData, FORM_CONSTANTS.SOURCE_DATA_CONSTANTS.COST_TABLE_BATTERY)
    },

    /** Select items */
    modelTypeItems() {
      return [
        {
          text: this.$t('complaintForm.ticket.battery.battery_pack'),
          // value: this.$t('complaintForm.ticket.battery.battery_pack'),
          value: 'battery_pack',
          image: get(this.imageData, 'battery'),
        },
        {
          text: this.$t('complaintForm.ticket.battery.loader'),
          // value: this.$t('complaintForm.ticket.battery.loader'),
          value: 'loader',
          image: get(this.imageData, 'loader'),
        },
        {
          text: this.$t('complaintForm.ticket.battery.battery_and_loader'),
          // value: this.$t('complaintForm.ticket.battery.battery_and_loader'),
          value: 'battery_and_loader',
          image: get(this.imageData, 'batteryAndLoader'),
        },
      ]
    },
    calculationWishSelectItems() {
      if (this.modelType === COMPLAINT_FORM_BATTERY_DATA_CONSTANTS.MODEL_TYPE_LOADER) {
        return [
          {
            text: this.$t('complaintForm.ticket.battery.calculation_request.1'),
            value: FORM_CONSTANTS.CALCULATION_DESIRE_TYPES.CALCULATION_DESIRE_GUARANTEE,
          },
          {
            text: this.$t('complaintForm.ticket.engine.calculation_request.3'),
            value: FORM_CONSTANTS.CALCULATION_DESIRE_TYPES.CALCULATION_DESIRE_PRODUCT_LIABILITY,
          },
        ]
      }

      return [
        {
          text: this.$t('complaintForm.ticket.battery.calculation_request.2'),
          value: FORM_CONSTANTS.CALCULATION_DESIRE_TYPES.CALCULATION_DESIRE_REPAIR_JOB,
        },
        {
          text: this.$t('complaintForm.ticket.battery.calculation_request.1'),
          value: FORM_CONSTANTS.CALCULATION_DESIRE_TYPES.CALCULATION_DESIRE_GUARANTEE,
        },
        {
          text: this.$t('complaintForm.ticket.engine.calculation_request.3'),
          value: FORM_CONSTANTS.CALCULATION_DESIRE_TYPES.CALCULATION_DESIRE_PRODUCT_LIABILITY,
        },
      ]
    },

    /** Errors */
    modelTypeErrors() {
      return this.formErrorHelper('modelType', [this.chooseError])
    },
    batteryArticleNoErrors() {
      return this.formErrorHelper('formData.batteryArticleNo', [
        this.requiredError,
        this.numericError,
        this.minLengthError(this.batteryArticleNoMinLength),
      ])
    },
    batterySerialNoErrors() {
      return this.formErrorHelper('formData.batterySerialNo', [
        this.requiredError,
        this.minLengthError(this.batterySerialNoMinLength),
      ])
    },
    loaderArticleNoErrors() {
      return this.formErrorHelper('formData.loaderArticleNo', [
        this.requiredError,
        this.numericError,
        this.minLengthError(this.laoderArticleNoMinLength),
      ])
    },
    loaderYearOfConstructionErrors() {
      return this.formErrorHelper('formData.loaderYearOfConstruction', [
        this.requiredError,
        this.numericError,
        this.minLengthError(this.loaderYearOfConstructionMinLength),
      ])
    },
    calculationDesireErrors() {
      return this.formErrorHelper('formData.calculationDesire', [this.chooseError])
    },
    serialNumberBikeErrors() {
      return this.formErrorHelper('serialNumberBike', [
        this.requiredError,
        this.numericError,
        this.minLengthError(this.serialNumberBikeMaxLength),
        this.maxLengthError(this.serialNumberBikeMaxLength),
      ])
    },
    saleDateErrors() {
      return this.formErrorHelper('formData.saleDate', [this.requiredError])
    },
    fileUploadErrors() {
      return compact([
        this.proofOfPurchaseError,
        ...this.formErrorHelper('formData.fileUpload', [
          {
            condition: 'required',
            error: this.fileErrorMessage,
          },
        ]),
      ])
    },
    accidentDateErrors() {
      return this.formErrorHelper('formData.accidentDate', [this.requiredError])
    },
    accidentReportErrors() {
      return this.formErrorHelper('formData.accidentReport', [this.requiredError])
    },
  },
  watch: {
    modelTypeItems: {
      handler: function (newValue, oldValue) {
        if (newValue !== oldValue) {
          this.modelType = get(this.modelTypeItems, '0.value', null)
        }
      },
    },
    'formData.calculationDesire'(newValue, oldValue) {
      if (oldValue === newValue) {
        return
      }

      if (this.isRepairJobType) {
        this.proofOfPurchase = null
        this.formData.saleDate = null
        this.formData.fileUpload = null
        this.formData.accidentDate = null
        this.formData.accidentReport = null
      }

      if (this.isWarantyType) {
        this.formData.accidentDate = null
        this.formData.accidentReport = null
      }
    },
    proofOfPurchase(newFiles, oldFiles) {
      if (oldFiles === newFiles) {
        return
      }

      if (this.proofOfPurchase?.length === 0) {
        this.proofOfPurchaseError = ''
        this.formData.fileUpload = ''
      }
    },
  },
  methods: {
    ...mapMutations(FORM_CONSTANTS.STORE_ID, {
      updateFormData: FORM_MUTATION.UPDATE_FORM_DATA,
      setPending: FORM_MUTATION.SET_FORM_PENDING,
    }),
    submitCallback() {
      this.updateFormData(this.fullFormData)
      this.nextStep()
    },
    setFileUploadPath({ response }) {
      const fileUploadPath = get(response, 'files.0', '')
      this.formData.fileUpload = get(response, 'files.0', '')

      if (!fileUploadPath) {
        const fileUploadError = get(response, 'error')

        if (fileUploadError) {
          this.proofOfPurchaseError = this.$t('form.dropzone.uploadFailed')
        }
      }
    },
    onModelTypeClick(val) {
      if (val === this.prevModelType) {
        this.modelType = null
      }

      this.prevModelType = this.modelType
    },
  },
}
</script>

<style lang="scss" module>
.pre {
  white-space: pre-line;
}
</style>
